/**
 * ``useNotify`` — the dispatch seam every notification producer calls.
 *
 * Thin by design: it exists so producers do not reach into the store
 * directly, and so the display-only decision lives in exactly one place.
 *
 * # The display-only seam (REQ-UI-056 / feature-spec §0.1 D4)
 *
 * Display-only mode gives an otherwise-sticky toast a bounded expiry, so an
 * unattended wall display self-heals instead of filling with alerts nobody
 * can dismiss. That behaviour is implemented and tested in
 * :func:`notification-timing.notificationDurationMs`.
 *
 * **It is not active yet, because display-only mode itself is unbuilt.**
 * REQ-UI-056 is `Not started` in the traceability matrix (gap-report
 * GAP-37): the BFF's ``display_only_enter/exit`` endpoints exist but the UI
 * mode does not, so there is no state to read. This hook is the documented
 * choke point — when REQ-UI-056 lands, ``displayOnly`` is sourced here and
 * every producer inherits the behaviour with no other edit.
 *
 * This follows the established house pattern for the same situation: the
 * Events panel and the panel maximize-toggle both landed
 * display-only-ungated with the gate flagged, "gated centrally when it
 * lands" (traceability-matrix.md, 2026-05-28 / 2026-05-29 entries).
 *
 * Cross-references:
 *   - REQ-UI-056 (display-only mode); gap-report GAP-37.
 *   - feature-specs/alert-notification-surface/feature-spec.md §0.1 D4.
 */
import { useCallback } from "react";
import { useNotificationStore } from "@/notifications/notification-store";
import type { NotificationId, NotificationInput } from "@/notifications/notification-types";

/**
 * Returns a stable ``notify`` for raising a notification.
 *
 * The returned function is referentially stable across renders (the store's
 * action identity never changes), so it is safe in an effect's dependency
 * array — which is how the SAPIENT-alert SSE bridge consumes it.
 */
export function useNotify(): (input: NotificationInput) => NotificationId {
  const notify = useNotificationStore((s) => s.notify);
  return useCallback(
    (input: NotificationInput) =>
      notify(input, {
        // The single source for this flag once REQ-UI-056 lands. Until then
        // there is no display-only state in the UI to read — see the module
        // docstring; do NOT scatter this decision into producers.
        displayOnly: false,
      }),
    [notify],
  );
}

/**
 * Returns the local-close actions: ``close`` by id and ``closeByDedupeKey``.
 *
 * Neither performs a system-wide alert dismissal — see
 * :file:`notification-types.ts` §"Local close vs system-wide dismissal".
 * ``closeByDedupeKey`` is what the cross-session dismissal fan-out uses to
 * retract a live notification for an alert another operator just handled.
 */
export function useCloseNotification(): {
  close: (id: NotificationId) => void;
  closeByDedupeKey: (dedupeKey: string) => void;
} {
  const close = useNotificationStore((s) => s.close);
  const closeByDedupeKey = useNotificationStore((s) => s.closeByDedupeKey);
  return { close, closeByDedupeKey };
}
